/**
 * LinkedIn Message Event Component
 *
 * Handles ahoy_linkedin_message_sent and ahoy_linkedin_message_received events.
 *
 * Display pattern:
 * - Header: "[source] sent/received LinkedIn message" (source from event.source_id)
 * - Collapsed: 3-line snippet with blur-fade
 * - Expanded: full message content
 * - Link to conversation in source app (when available)
 */

import { LinkExternal01 } from '@untitledui/icons';

import { CollapsibleContent } from '@/lib/base/collapsible-content';

import { ActorName, InlineText, TimelineEvent } from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { LinkedInMessagePayload } from '../types';
import { EVENT_TYPES } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import { isValidHttpUrl } from '../utils/url';
import type { EventComponentProps } from './types';

export function LinkedInMessageEvent({ event }: EventComponentProps) {
  const { lookups } = useViewStateLookups();
  const { getActorInfo } = useEventFormatters();
  const payload = event.payload as LinkedInMessagePayload | undefined;
  const isSent = event.event_type_name === EVENT_TYPES.LINKEDIN_MESSAGE_SENT;
  const actor = getActorInfo(event);

  const messageContent = payload?.properties?.ahoy_message_content;

  // Extract from/to entity IDs and resolve to CrmObjects
  const fromEntityId = payload?.from;
  const toEntityId = payload?.to;
  const fromEntity = fromEntityId ? lookups.entities[fromEntityId] : undefined;
  const toEntity = toEntityId ? lookups.entities[toEntityId] : undefined;
  const fromName = fromEntity?.properties?.ahoy_display_name?.value as
    | string
    | undefined;
  const toName = toEntity?.properties?.ahoy_display_name?.value as
    | string
    | undefined;

  // Get the external URL from the conversation entity's properties
  const conversationEntity = lookups.entities[event.entity_id];
  const rawExternalUrl = conversationEntity?.properties?.ahoy_external_url
    ?.value as string | undefined;
  // Only use URL if it's a valid http/https URL (prevents XSS via javascript: scheme)
  const externalUrl =
    rawExternalUrl && isValidHttpUrl(rawExternalUrl)
      ? rawExternalUrl
      : undefined;

  const sourceId = event.source_id;
  const linkLabel = sourceId
    ? `View in ${sourceId.charAt(0).toUpperCase()}${sourceId.slice(1)}`
    : 'View conversation';

  const ariaLabel = isSent
    ? `${actor.name} sent LinkedIn message`
    : `${actor.name} received LinkedIn message`;

  const metaTextClass = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;

  const headerContent = isSent ? (
    <>
      <ActorName actor={actor} /> sent LinkedIn message
    </>
  ) : (
    <>
      <ActorName actor={actor} /> received LinkedIn message
    </>
  );

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={<InlineText>{headerContent}</InlineText>}
        timestamp={event.occurred_at}
      >
        {/* Show participant info if available */}
        {!isSent && fromName && (
          <div className={metaTextClass}>From: {fromName}</div>
        )}
        {isSent && toName && <div className={metaTextClass}>To: {toName}</div>}

        {messageContent && (
          <div className='mt-2'>
            <CollapsibleContent maxCollapsedHeight={63}>
              <div className={`${metaTextClass} whitespace-pre-wrap`}>
                {messageContent}
              </div>
            </CollapsibleContent>
            {externalUrl && (
              <div className='mt-1'>
                <a
                  href={externalUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex items-center gap-1 text-xs text-fg-brand-primary hover:underline'
                >
                  {linkLabel} <LinkExternal01 className='w-3 h-3' />
                </a>
              </div>
            )}
          </div>
        )}

        {/* Show external link even if no message content */}
        {!messageContent && externalUrl && (
          <div className='mt-2'>
            <a
              href={externalUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1 text-xs text-fg-brand-primary hover:underline'
            >
              {linkLabel} <LinkExternal01 className='w-3 h-3' />
            </a>
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
